import styled from 'styled-components';
import { Palette } from 'core/config';
import { isChained, isRequired, normalizeCurrency, isValidIncome } from 'core/utilities';
import { Button, CardBody, CardFooter, FormStep, InputType } from 'view/components/common';

const Introduction : React.FunctionComponent<{ onContinue: () => void }> = ({ onContinue }) =>
{
    return (
        <Container>
            <CardBody>
                <Paragraph>
                    Thank you for sharing your employment information with us!
                </Paragraph>
                <Paragraph>
                    <span>Step 3</span> in obtaining a mortgage from Manzil is to tell us about your financial situation, including your debts and assets.
                </Paragraph>
                <Paragraph>
                    Continue when you are ready.
                </Paragraph>
            </CardBody>
            <CardFooter>
                <Button 
                    title="Continue" 
                    type="button" 
                    onClick={onContinue}
                />
            </CardFooter>
        </Container>
    );
};

export const formSteps = [
    [
        {
            type : InputType.info,
            Component : Introduction
        }
    ],
    [
        {
            type : InputType.income,
            name : 'salary',
            label : 'What do you expect to earn in income this year?',
            placeholder : '$120,000',
            required : true, 
            validator : isChained([ isValidIncome, isRequired ]),
            normalizer : normalizeCurrency
        },
        {
            type : InputType.info,
            Component : () => (
                <Label>
                    Including salary, commission, bonuses and anything else you expect to earn this year.
                </Label>
            )
        },
        {
            type : InputType.toggle,
            name : 'has_investment_properties',
            label : 'Do you own any investment properties?'
        }
    ], 
    [ 
        { 
            type : InputType.location,
            name : 'full_address',
            label : 'What is the address of your investment property?',
            placeholder : '100 King Street West, Toronto, ON, Canada'
        },
        {
            type : InputType.text,
            name : 'investment_property_value',
            label : 'What do you estimate is the value of the property?',
            placeholder : '$500,000',
            validator : isRequired,
            normalizer : normalizeCurrency
        },
        {
            type : InputType.text,
            name : 'investment_property_balance_owing',
            label : 'What is the balance owing on the mortgage?',
            placeholder : '$350,000',
            validator : isRequired,
            normalizer : normalizeCurrency
        },
        {
            type : InputType.text,
            name : 'investment_property_monthly_mortgage_payments',
            label : 'How much are the monthly mortgage payments?',
            placeholder : '$2,000',
            validator : isRequired,
            normalizer : normalizeCurrency
        },
        {
            type : InputType.text,
            name : 'investment_property_monthly_property_taxes',
            label : 'How much are the property taxes?',
            placeholder : '$2,000',
            validator : isRequired,
            normalizer : normalizeCurrency
        }
    ],
    [
        {
            type : InputType.info,
            Component : () => (
                <Label css='margin-top: -10px;'>
                    How much money do you have in each of the following investment accounts?
                </Label>
            )
        },
        {
            type : InputType.text,
            name : 'non_reg_cash',
            label : 'Non-registered/Cash',
            placeholder : '$15,000',
            required : true, 
            validator : isRequired,
            normalizer : normalizeCurrency
        },
        {
            type : InputType.text,
            name : 'rrsp',
            label : 'RRSP',
            placeholder : '$50,000',
            required : true, 
            validator : isRequired,
            normalizer : normalizeCurrency
        },
        {
            type : InputType.text,
            name : 'tfsa',
            label : 'TFSA',
            placeholder : '$25,000',
            required : true, 
            validator : isRequired,
            normalizer : normalizeCurrency
        },
        {
            type : InputType.text,
            name : 'resp',
            label : 'RESP',
            placeholder : '$5,000',
            required : true, 
            validator : isRequired,
            normalizer : normalizeCurrency
        }
    ],
    [ // TODO check if user owns property -- if so, display this step, else skip
        // https://speertech.atlassian.net/jira/software/projects/MANZIL/boards/6?selectedIssue=MANZIL-72
        {
            type : InputType.text,
            name : 'property_value',
            label : 'What is the property value of your home?',
            placeholder : '$1,234,567',
            required : true, 
            validator : isRequired,
            normalizer : normalizeCurrency
        },
        {
            type : InputType.text,
            name : 'mortgage_balance',
            label : 'What is the balance owing on your mortgage?',
            placeholder : '$500,000',
            required : true, 
            validator : isRequired,
            normalizer : normalizeCurrency
        },
        {
            type : InputType.text,
            name : 'monthly_mortgage',
            label : 'How much are your monthly mortgage payments?',
            placeholder : '$5,000',
            required : true, 
            validator : isRequired,
            normalizer : normalizeCurrency
        },
        {
            type : InputType.text,
            name : 'property_tax',
            label : 'What are your monthly property taxes?',
            placeholder : '$1,200',
            required : true, 
            validator : isRequired,
            normalizer : normalizeCurrency
        }
    ],
    [
        {
            type : InputType.text,
            name : 'credit_debt_amount',
            label : 'What is your total credit card debt?',
            placeholder : '$5,000',
            required : true, 
            validator : isRequired,
            normalizer : normalizeCurrency
        },
        {
            type : InputType.text,
            name : 'credit_debt_monthly_payment',
            label : 'How much are your total monthly payments?',
            placeholder : '$200',
            required : true, 
            validator : isRequired,
            normalizer : normalizeCurrency
        },
        {
            type : InputType.info,
            Component : () => (
                <Label>
                    This is the total sum of all your credit cards.
                </Label>
            )
        }
    ],
    [
        {
            type : InputType.text,
            name : 'credit_line_amount',
            label : 'What is your total line of credit debt?',
            placeholder : '$100,000',
            required : true, 
            validator : isRequired,
            normalizer : normalizeCurrency
        },
        {
            type : InputType.text,
            name : 'credit_line_monthly_payment',
            label : 'How much are your total monthly payments?',
            placeholder : '$2,000',
            required : true, 
            validator : isRequired,
            normalizer : normalizeCurrency
        },
        {
            type : InputType.info,
            Component : () => (
                <Label>
                    This is the total sum of all your lines of credit.
                </Label>
            )
        }
    ],
    [
        {
            type : InputType.text,
            name : 'other_debt_amount',
            label : 'How much do you owe in total in other loans?',
            placeholder : '$25,000',
            required : true, 
            validator : isRequired,
            normalizer : normalizeCurrency
        },
        {
            type : InputType.text,
            name : 'other_debt_monthly_payment',
            label : 'How much are your total monthly payments?',
            placeholder : '$200',
            required : true, 
            validator : isRequired,
            normalizer : normalizeCurrency
        },
        {
            type : InputType.info,
            Component : () => (
                <Label>
                    This is the total sum of all the other loans you might have.
                </Label>
            )
        }
    ],
    [
        {
            type : InputType.text,
            name : 'car_debt_amount',
            label : 'How much do you owe in total in car loans?',
            placeholder : '$5,000',
            required : true, 
            validator : isRequired,
            normalizer : normalizeCurrency
        },
        {
            type : InputType.text,
            name : 'car_debt_monthly_payment',
            label : 'How much are your total monthly payments?',
            placeholder : '$500',
            required : true, 
            validator : isRequired,
            normalizer : normalizeCurrency
        },
        {
            type : InputType.info,
            Component : () => (
                <Label>
                    This is the total sum of all the car loans you might have.
                </Label>
            )
        } 
    ]
] as Array<FormStep>;

const Container = styled.div`
    margin-top: 17.5px;
`;

const Paragraph = styled.p`
    padding-top: 6px;

    :first-child {
        padding-top: 15px;
    }

    padding-left: 60px;
    padding-right: 60px;
    font: normal normal normal 10px 'SofiaPro-Regular';
    color: ${Palette.BLUE};
    font-weight: 200;

    span {
        font: normal normal normal 10px 'SofiaPro-Bold';
    }

    @media only screen and (min-width: 1300px) {
        font-size: 12px;

        span {
            font-size: 12px;
        }

        a {
            font-size: 12px;
        }
    }

    @media only screen and (min-width: 1900px) {
        font-size: 14px;

        span {
            font-size: 14px;
        }

        a {
            font-size: 14px;
        }
    }
`;

const Label = styled.div<{ css? : string; }>`
    margin-left: 60px;
    margin-right: 60px;
    margin-top: 10px;
    margin-bottom: 5px;
    color: ${Palette.BLUE};
    font-size: 10px;
    font-family: 'SofiaPro-Regular';

    @media only screen and (min-width: 1300px) {
        font-size: 12px;

        span {
            font-size: 12px;
        }

        a {
            font-size: 12px;
        }
    }

    @media only screen and (min-width: 1900px) {
        font-size: 14px;

        span {
            font-size: 14px;
        }

        a {
            font-size: 14px;
        }
    }

    ${({ css }) => css && css}
`;